import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronRight, Loader2, AlertTriangle, Check, X, ChevronDown } from 'lucide-react';
import {
  getPrompt, updatePrompt, createPrompt, createVersion, setVersionLabel, removeVersionLabel,
  listPrompts, listLabels, addLabel as addTeamLabel, listTeamMembers,
} from 'features/prompts/api/promptsApi';
import { PageLoader } from 'shared/components/ui/BrandLoader';
import { useHideGlobalTopBar } from 'shared/context/BreadcrumbContext';
import MessagesEditor from 'features/prompts/components/MessagesEditor';
import VariablesPanel from 'features/prompts/components/VariablesPanel';
import JsonEditor from 'features/prompts/components/JsonEditor';
import ModelConfigPanel from 'features/prompts/components/ModelConfigPanel';
import { OutputFormatMenu, OutputSchemaEditor, kindOf as outputKindOf } from 'features/prompts/components/OutputFormatPanel';
import SdkCodeModal from 'features/prompts/components/SdkCodeModal';
import DiffView from 'features/prompts/components/DiffView';
import BrandSelect from 'features/playground/components/BrandSelect';
import { createPlayground } from 'features/playground/api/playgroundApi';
import VersionTimeline from 'features/prompts/components/VersionTimeline';
import VersionPopup from 'features/prompts/components/VersionPopup';
import MembersPanel from 'features/prompts/components/MembersPanel';
import { listIntegrations } from 'features/playground/api/playgroundApi';
import { useCatalogModels } from 'shared/hooks/useCatalogModels';
import {
  emptyDraft, reconcileVariables, messageText, sectionInnerNames,
  newMessage, newPlaceholder,
} from 'features/prompts/lib/promptDraft';
import { sameDraft, draftToLines, diffLines } from 'features/prompts/lib/promptDiff';
import { drawerIconButtonClass, activityToolbarButtonClass } from 'features/activity/styles/dataTableStyles';
import { shortHash } from 'features/prompts/lib/promptMembers';

const controlCls = 'h-8 rounded-md border border-[color:var(--border-default)] bg-transparent px-2.5 text-[12px] text-[color:var(--text-primary)] focus:outline-none focus:border-[color:var(--border-strong)] focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]';
const inputCls = `w-full ${controlCls} placeholder:text-[color:var(--text-disabled)]`;
const labelCls = 'block text-[12px] font-medium text-[color:var(--text-muted)] mb-1.5';
const neutralBtnCls = 'inline-flex h-8 items-center justify-center rounded-md border border-[color:var(--border-default)] bg-surface px-3 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';
const primaryBtnCls = 'inline-flex h-8 items-center justify-center rounded-md bg-[color:var(--trodo-primary)] px-3 text-[12px] font-medium text-[color:var(--text-on-brand)] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap';


// Collapsible sections under the messages. Model config, Tools and Output
// format are raw-JSON editors (the exact shape compile() returns); Variables
// keeps its purpose-built panel.
const SECTIONS = [
  ['model', 'Model config'],
  ['variables', 'Variables'],
  ['tools', 'Tools'],
];

/**
 * The name is the key the SDK fetches by, so it lives in the same character set
 * on both sides: spaces collapse to dashes, anything else unsafe is dropped.
 * Applied as you type so what's in the field is exactly what gets saved.
 */
export function normalizePromptName(raw) {
  return String(raw || '')
    .replace(/\s+/g, '-')
    .replace(/[^a-zA-Z0-9._-]/g, '')
    .slice(0, 120);
}

/** Drop empty-string / null values so a blank field never trips validation,
 *  while leaving any extra keys the author typed intact. */
function pruneBlank(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj;
  const out = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v === '' || v === null || v === undefined) continue;
    out[k] = v;
  }
  return out;
}

// The shared outlined-button look for the toolbar row (Add message + sections).
const toolBtnCls = 'inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-surface px-2.5 text-[12px] font-medium text-[color:var(--text-primary)] transition-colors hover:bg-surface-hover disabled:opacity-50';

const ADD_ITEMS = [
  ['user', 'User'],
  ['system', 'System'],
  ['assistant', 'Assistant'],
  ['placeholder', 'Placeholder'],
];

/** "Add message" — an outlined dropdown offering the three roles plus a
 *  placeholder. Every role can be added any number of times, system included. */
function AddMessageMenu({ disabled, onAdd }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} className={toolBtnCls}>
        Add message
        <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''} text-[color:var(--text-muted)]`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-[calc(100%+4px)] z-30 min-w-[160px] overflow-hidden rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] py-1 shadow-lg">
            {ADD_ITEMS.map(([kind, label]) => (
              <button
                key={kind}
                type="button"
                onClick={() => { onAdd(kind); setOpen(false); }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[12.5px] text-[color:var(--text-secondary)] hover:bg-[color:var(--surface-hover)]"
              >
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function PromptEditorPage({ promptId }) {
  const { team } = useParams();
  const navigate = useNavigate();
  // No id yet = an unsaved draft (`/prompts/new`). Nothing exists server-side
  // until the first save, which is also the moment the name stops being
  // editable — it becomes the immutable fetch key from then on.
  const isNew = !promptId;
  useHideGlobalTopBar(true);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [showCodeModal, setShowCodeModal] = useState(false);
  // Timeline → click a version to open its detail popup (view / load / diff).
  const [selectedVersion, setSelectedVersion] = useState(null);
  // Per-prompt access.
  const [members, setMembers] = useState([]); // team members {userId,name,email}
  // Linked provider keys — the Configure tab offers these, and their catalogs
  // supply the model lists. Fail-open: no integrations just means the model
  // block is JSON-only, which is how it worked before.
  const [integrations, setIntegrations] = useState([]);
  const [canEdit, setCanEdit] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [ownerId, setOwnerId] = useState(null);
  const [showMembers, setShowMembers] = useState(false);

  const [prompt, setPrompt] = useState(null);
  const [name, setName] = useState('');
  // Draft-mode name validation, shown under the field.
  const [nameError, setNameError] = useState(null);
  const nameRef = React.useRef(null);
  // Existing names on this team, so a clash is caught as you type rather than
  // only when the save round-trips. The server is still the authority.
  const [takenNames, setTakenNames] = useState(null);
  const [description, setDescription] = useState('');
  const [draft, setDraft] = useState(emptyDraft());
  // Snapshot of the version currently loaded into the editor — the baseline the
  // draft is diffed against for dirty-detection and the save-modal diff. null
  // means nothing loaded yet (a brand-new prompt with no versions).
  const [baseline, setBaseline] = useState(null);
  const [baselineVersionNo, setBaselineVersionNo] = useState(null);
  const [commitMessage, setCommitMessage] = useState('');
  const [versions, setVersions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState([]);
  const [notice, setNotice] = useState(null);

  // team labels + save-version modal
  const [teamLabels, setTeamLabels] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [chosenLabel, setChosenLabel] = useState('');
  const [addingLabel, setAddingLabel] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  // Second-stage confirm shown when saving would move a label off another version.
  const [confirmMove, setConfirmMove] = useState(false);

  const modelsForCredential = useCatalogModels(null, integrations);

  // Line-by-line diff of the loaded version → current draft, shown in the save
  // modal. Cheap for a prompt's line count, but memoised so it isn't recomputed
  // on unrelated re-renders.
  const diff = useMemo(
    () => diffLines(draftToLines(baseline), draftToLines(draft)),
    [baseline, draft],
  );
  const load = useCallback(async () => {
    if (!promptId) return; // draft: there's nothing to fetch yet
    setError(null);
    try {
      const p = await getPrompt(promptId);
      setPrompt(p);
      setName(p.name || '');
      setDescription(p.description || '');
      setVersions(p.versions || []);
      // Access flags come from the server for the current user.
      setCanEdit(p.can_edit !== false);
      setIsOwner(Boolean(p.is_owner));
      setOwnerId(p.owner_id ?? p.created_by ?? null);
      // A `?version=<hash>` deep link opens the exact version that ran, which
      // is how a trace hands you a prompt: the span recorded an immutable hash,
      // and landing on "latest" would show something that span never executed.
      // A prefix is accepted because the UI displays a short hash. Unknown or
      // absent -> latest, so a stale link degrades instead of breaking.
      const wantHash = new URLSearchParams(window.location.search).get('version');
      const pinned = wantHash
        ? (p.versions || []).find((v) => v.version_hash === wantHash)
          || (p.versions || []).find((v) => String(v.version_hash || '').startsWith(wantHash))
        : null;
      const latest = pinned
        || (p.versions || []).find((v) => v.version_no === p.current_version)
        || (p.versions || [])[0];
      if (latest) {
        const loaded = {
          messages: latest.messages || [],
          model: latest.model || {},
          tools: latest.tools || [],
          response_format: latest.response_format ?? null,
          variables: latest.variables || [],
        };
        setDraft(loaded);
        setBaseline(loaded);
        setBaselineVersionNo(latest.version_no);
      } else {
        setBaseline(null);
        setBaselineVersionNo(null);
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  }, [promptId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { listLabels().then(setTeamLabels).catch(() => setTeamLabels(['production', 'staging'])); }, []);
  useEffect(() => {
    if (!isNew) return;
    listPrompts()
      .then((ps) => setTakenNames(new Set(ps.map((p) => (p.name || '').toLowerCase()))))
      .catch(() => setTakenNames(null)); // couldn't check: the server still will
  }, [isNew]);
  // Team members: for resolving a version's author email → name, and for the
  // owner's invite picker.
  useEffect(() => { listTeamMembers().then(setMembers).catch(() => setMembers([])); }, []);
  useEffect(() => { listIntegrations().then(setIntegrations).catch(() => setIntegrations([])); }, []);

  // Keep declarations in step with what the messages actually reference. The
  // server reconciles authoritatively on save; this just stops the panel from
  // lagging a keystroke behind.
  const setMessages = (messages) => setDraft((d) => ({
    ...d,
    messages,
    variables: reconcileVariables(messages, d.variables),
  }));

  // Add a message from the toolbar dropdown. A placeholder gets a fresh unique
  // name; every role (system included) simply appends.
  const addMessageKind = (kind) => {
    const msgs = draft.messages;
    if (kind === 'placeholder') {
      const taken = new Set(msgs.filter((m) => m.type === 'placeholder').map((m) => m.name));
      let nm = 'chat_history';
      let n = 2;
      while (taken.has(nm)) { nm = `chat_history_${n}`; n += 1; }
      setMessages([...msgs, newPlaceholder(nm)]);
    } else {
      setMessages([...msgs, newMessage(kind)]);
    }
  };

  const nextVersionNo = (prompt?.current_version || 0) + 1;
  const labelHolder = chosenLabel ? versions.find((v) => (v.labels || []).includes(chosenLabel)) : null;
  // Versions are referenced by hash in the UI, never a v-number. The new version
  // has no hash until the server cuts it, so its save affordances stay generic.
  const baselineHash = baselineVersionNo != null
    ? shortHash(versions.find((v) => v.version_no === baselineVersionNo)?.version_hash)
    : null;
  // Saving with a label that another version currently holds will MOVE it — that
  // displacement is what we make the user confirm before writing.
  const labelMoves = Boolean(chosenLabel && labelHolder && labelHolder.version_no !== nextVersionNo);

  // Dirty against the loaded version. A brand-new prompt (no baseline) is
  // always "dirty" — there's nothing saved yet to match.
  const isDirty = !baseline || !sameDraft(draft, baseline);

  // A draft has to be named before it can be saved: no "untitled" prompts, and
  // no two prompts sharing a name on the same team.
  const trimmedName = name.trim();
  const nameProblem = (() => {
    if (!isNew) return null;
    if (!trimmedName) return 'Set a name. It can’t be changed after saving.';
    if (takenNames?.has(trimmedName.toLowerCase())) {
      return `"${trimmedName}" is already used by another prompt on this team.`;
    }
    return null;
  })();

  // What the "Code" snippet should fetch: the SAME version loaded in the editor
  // — but ONLY while the draft still matches it exactly (not dirty). The moment
  // you edit away from a loaded version, that version's label/hash no longer
  // describes what's in the editor (it's neither saved nor deployed), so the
  // snippet falls back to the generic `production` default rather than
  // pointing at stale content. Once saved (or if nothing was ever loaded),
  // priority is: a label on the loaded version → that label (preferring
  // `production` if it's among them); no label → the loaded version's own
  // hash; nothing loaded / unsaved → the `production` default a save would
  // follow.
  const loadedVersion = (!isDirty && baselineVersionNo != null)
    ? versions.find((v) => v.version_no === baselineVersionNo)
    : null;
  const codeSelector = (() => {
    const labels = loadedVersion?.labels || [];
    if (labels.length > 0) {
      return { kind: 'label', value: labels.includes('production') ? 'production' : labels[0] };
    }
    if (loadedVersion?.version_hash) {
      return { kind: 'hash', value: loadedVersion.version_hash };
    }
    return { kind: 'label', value: 'production' };
  })();

  const commitDescription = async () => {
    // On a draft the description just rides along with the create call.
    if (!prompt || description === (prompt.description || '')) return;
    try {
      const updated = await updatePrompt(promptId, { description });
      setPrompt((p) => ({ ...p, description: updated.description }));
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };

  const handleSaveVersion = async () => {
    setError(null);
    setDetails([]);
    setNotice(null);
    setConfirmMove(false);
    const lbl = chosenLabel;
    // For a draft this save is also the creation: the prompt row is written
    // first, then its first version. Only after that does the name lock.
    if (isNew && nameProblem) {
      setNameError(nameProblem);
      setShowSaveModal(false);
      return;
    }
    setSaving(true);
    let targetId = promptId;
    try {
      if (isNew) {
        const created = await createPrompt(trimmedName, description.trim() || undefined);
        targetId = created.id;
      }
      const v = await createVersion(targetId, {
        messages: draft.messages,
        model: pruneBlank(draft.model || {}),
        tools: draft.tools || [],
        response_format: draft.response_format ?? null,
        variables: draft.variables || [],
        labels: lbl ? [lbl] : [],
        commit_message: commitMessage.trim() || undefined,
      });
      setCommitMessage('');
      setChosenLabel('');
      setAddingLabel(false);
      setNewLabel('');
      setShowSaveModal(false);
      setNameError(null);
      if (isNew) {
        // Swap the draft URL for the real one; the id change re-runs load().
        navigate(`/${encodeURIComponent(team)}/prompts/${targetId}`, { replace: true });
      } else {
        await load();
      }
      // git-style: identical content doesn't cut a new version. Reference the
      // version by its hash, never a v-number.
      const h = shortHash(v.version_hash) || 'the current version';
      const base = v.unchanged ? `No changes. Still on ${h}.` : `Saved ${h}.`;
      setNotice(base + (lbl ? ` Pushed to ${lbl}.` : ''));
      setTimeout(() => setNotice(null), 4500);
    } catch (e) {
      const data = e?.response?.data;
      // The create leg failed — the prompt was never written, so keep the draft
      // exactly as it is and send the user back to the name field.
      if (isNew && targetId === promptId) {
        if (e?.response?.status === 409 || data?.error === 'name_taken') {
          setNameError(`"${trimmedName}" is already used by another prompt on this team.`);
          setTakenNames((prev) => new Set([...(prev || []), trimmedName.toLowerCase()]));
        } else if (data?.error === 'name_required' || data?.error === 'invalid_name') {
          setNameError('Use letters, numbers, dashes, dots or underscores.');
        } else {
          setError(data?.error || e.message);
        }
        setShowSaveModal(false);
        return;
      }
      // The prompt was created but its first version didn't land. Move onto the
      // real prompt so the name isn't stranded on a URL that can't retry it.
      if (isNew) navigate(`/${encodeURIComponent(team)}/prompts/${targetId}`, { replace: true });
      // Validation returns every problem at once, so show them all rather than
      // making the user fix one per round-trip.
      if (data?.error === 'invalid_content' && Array.isArray(data.details)) {
        setError('This version isn’t valid yet.');
        setDetails(data.details);
        setShowSaveModal(false);
      } else {
        setError(data?.error || e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const confirmAddLabel = async () => {
    const l = newLabel.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9_-]/g, '');
    if (!l) return;
    try {
      const labels = await addTeamLabel(l);
      setTeamLabels(labels);
      setChosenLabel(l);
      setAddingLabel(false);
      setNewLabel('');
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };

  // Register a brand-new team label (used by the version popup's "Create new
  // label…" flow). Returns the normalised name on success, null on failure —
  // the caller treats null as "stay put", the error banner shows why.
  const createTeamLabel = async (raw) => {
    const l = String(raw).trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9_-]/g, '');
    if (!l) return null;
    try {
      const labels = await addTeamLabel(l);
      setTeamLabels(labels);
      return l;
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      return null;
    }
  };

  const loadVersionIntoEditor = (v) => {
    const loaded = {
      messages: v.messages || [],
      model: v.model || {},
      tools: v.tools || [],
      response_format: v.response_format ?? null,
      variables: v.variables || [],
    };
    setDraft(loaded);
    setBaseline(loaded);
    setBaselineVersionNo(v.version_no);
    setVersionsOpen(false);
    setSelectedVersion(null);
  };

  const toggleLabelOnVersion = async (v, label) => {
    setError(null);
    try {
      if ((v.labels || []).includes(label)) {
        await removeVersionLabel(promptId, v.version_no, label);
      } else {
        await setVersionLabel(promptId, v.version_no, label);
      }
      await load();
    } catch (e) { setError(e?.response?.data?.error || e.message); }
  };

  if (!isNew && !prompt) {
    return error
      ? <div className="p-6 text-[12.5px] text-[color:var(--feedback-error)]">{error}</div>
      : <PageLoader label="Loading prompt" />;
  }

  const hasContent = draft.messages.length > 0
    && draft.messages.some((m) => m.type === 'placeholder' || messageText(m).trim());
  const canSave = hasContent && isDirty;

  return (
    <div className="flex h-full min-h-0 flex-col bg-[color:var(--surface-base)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      {/* Top bar */}
      <div className="flex h-12 shrink-0 items-center gap-2 overflow-x-auto border-b border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-4">
        <Link
          to={`/${encodeURIComponent(team)}/prompts`}
          className="shrink-0 whitespace-nowrap rounded text-[13px] text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]"
        >
          Prompts
        </Link>
        <ChevronRight className="h-3 w-3 shrink-0 text-[color:var(--text-muted)]" aria-hidden />
        {/* Once saved the name is the immutable fetch key: shown, never edited
            here. An unsaved draft is named in the field below. */}
        <span
          className={`min-w-0 truncate px-1.5 text-[13px] font-semibold ${
            name ? 'text-[color:var(--text-primary)]' : 'font-normal text-[color:var(--text-disabled)]'
          }`}
          title={name || 'Unsaved prompt'}
        >
          {name || 'New prompt'}
        </span>
        <div className="flex-1" />
        {/* Code snippets, access, usage and history all need a saved prompt to
            point at, so a draft shows none of them. */}
        {!isNew && (
          <>
            <button type="button" onClick={() => setShowCodeModal(true)} title="Use this prompt in code" className={activityToolbarButtonClass}>
              Code
            </button>
            <button type="button" onClick={() => setShowMembers(true)} title="Who can edit this prompt" className={activityToolbarButtonClass}>
              Editors
            </button>
            {/* Usage for the version currently loaded. A page of its own rather
                than a panel here: the request is scoped to this one version, so
                it loads the same whether the prompt has two versions or two hundred. */}
            <button
              type="button"
              onClick={() => {
                const live = versions.find((v) => v.version_no === baselineVersionNo) || versions[0];
                if (live?.version_hash) {
                  navigate(`/${encodeURIComponent(team)}/prompts/${promptId}/usage/${live.version_hash}`);
                }
              }}
              disabled={!versions.length}
              title={versions.length ? 'Usage for the loaded version' : 'Save a version first'}
              className={`${activityToolbarButtonClass} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              Usage
            </button>
            <button
              type="button"
              onClick={() => setVersionsOpen((o) => !o)}
              aria-pressed={versionsOpen}
              data-versions-trigger
              className={activityToolbarButtonClass}
            >
              History
            </button>
            <button
              type="button"
              className={activityToolbarButtonClass}
              onClick={async () => {
                const pg = await createPlayground(`Playground · ${name}`);
                navigate(`/${encodeURIComponent(team)}/playground/${pg.id}?prompt=${encodeURIComponent(name)}`);
              }}
            >
              Open in Playground
            </button>
          </>
        )}
        {isDirty && !isNew && <span className="shrink-0 text-[12px] text-[color:var(--text-muted)]">Unsaved changes</span>}
        <button
          type="button"
          onClick={() => {
            if (nameProblem) { setNameError(nameProblem); nameRef.current?.focus(); return; }
            setChosenLabel(''); setCommitMessage(''); setAddingLabel(false); setNewLabel(''); setConfirmMove(false); setShowSaveModal(true);
          }}
          disabled={saving || !canSave || !canEdit}
          title={
            !canEdit ? 'View-only: ask the owner for edit access'
              : (
                !hasContent ? 'Add message content to save a version'
                  : !isDirty ? 'No changes since the loaded version'
                    : isNew ? 'Create this prompt and save its first version'
                      : 'Save a new version')
          }
          className={`${primaryBtnCls} shrink-0`}
        >
          {isNew ? 'Create prompt' : 'Save'}
        </button>
      </div>

      {error && (
        <div className="mx-5 mt-3 rounded-lg border border-[color:var(--feedback-error)] bg-[color:var(--feedback-error-bg)] px-3 py-2 text-[12.5px] text-[color:var(--feedback-error)]">
          <div className="flex items-center gap-2"><AlertTriangle size={14} /> {error}</div>
          {details.length > 0 && (
            <ul className="mt-1.5 list-disc pl-7 text-[11.5px]">
              {details.map((d) => <li key={d}>{d}</li>)}
            </ul>
          )}
        </div>
      )}
      {notice && !error && (
        <div className="mx-5 mt-3 flex items-center gap-2 text-[12.5px] text-[color:var(--text-secondary)]">
          <Check size={14} /> {notice}
        </div>
      )}
      {prompt && !canEdit && (
        <div className="mx-5 mt-3 text-[12.5px] text-[color:var(--text-muted)]">
          View-only. Only the owner and invited editors can save changes.
        </div>
      )}

      <div className="relative flex min-h-0 flex-1 overflow-hidden">
        {/* Editor column */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-4">
          {/* Name sits inline with the description. It's typed here once, while
              the prompt is still a draft; after the first save it's the fetch
              key the SDK uses and can no longer change. */}
          <div className="flex items-start gap-3">
            <label className="block w-[220px] shrink-0">
              <span className={labelCls}>
                Name{isNew ? <span className="text-[color:var(--feedback-error)]" aria-hidden> *</span> : null}
              </span>
              {isNew ? (
                <input
                  autoFocus
                  ref={nameRef}
                  value={name}
                  onChange={(e) => { setName(normalizePromptName(e.target.value)); setNameError(null); }}
                  disabled={saving}
                  placeholder="e.g. refund-agent"
                  aria-invalid={Boolean(nameError)}
                  className={`${inputCls} font-mono ${nameError ? 'border-[color:var(--feedback-error)]' : ''}`}
                />
              ) : (
                <div
                  className={`${inputCls} flex items-center font-mono text-[color:var(--text-secondary)] cursor-not-allowed select-all`}
                  title="The fetch key (can't be changed after creation)"
                >
                  <span className="truncate">{name}</span>
                </div>
              )}
              {isNew && nameError && (
                <span className="mt-1 block text-[11px] text-[color:var(--feedback-error)]">{nameError}</span>
              )}
            </label>
            <label className="block flex-1">
              <span className={labelCls}>Description</span>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                onBlur={commitDescription}
                disabled={!canEdit}
                placeholder="What this prompt is for…"
                className={inputCls}
              />
            </label>
          </div>


          {/* Messages: always shown; this is the prompt. */}
          <div className="mt-4">
            <div className="mb-2 flex items-center gap-2">
              <span className="text-[13px] font-semibold text-[color:var(--text-primary)]">Messages</span>
              {draft.messages.length > 0 && (
                <span className="text-[11px] tabular-nums text-[color:var(--text-muted)]">{draft.messages.length}</span>
              )}
            </div>
            <MessagesEditor messages={draft.messages} onChange={setMessages} disabled={saving || !canEdit} />
          </div>

          {/* Toolbar: add a message, or open a config section. One row. */}
          <div className="mt-4 flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <AddMessageMenu disabled={saving || !canEdit} onAdd={addMessageKind} />
              
              {SECTIONS.map(([key, label]) => {
                const summary = key === 'variables' ? (draft.variables.length || null)
                  : key === 'tools' ? ((draft.tools || []).length || null)
                    : key === 'model' ? (draft.model?.model || null)
                      : (draft.response_format ? outputKindOf(draft.response_format) : null);
                const active = openSection === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setOpenSection(active ? null : key)}
                    aria-expanded={active}
                    className={`${toolBtnCls} ${active ? 'border-[color:var(--border-strong)] bg-surface-hover' : ''}`}
                  >
                    {label}
                    {summary != null && (
                      <span className="text-[11px] font-normal text-[color:var(--text-muted)]">
                        {summary}
                      </span>
                    )}
                    <ChevronDown
                      size={13}
                      className={`transition-transform ${active ? 'rotate-180' : ''} text-[color:var(--text-muted)]`}
                    />
                  </button>
                );
              })}
              <OutputFormatMenu
                value={draft.response_format ?? null}
                disabled={saving || !canEdit}
                active={openSection === 'output'}
                onChange={(response_format) => setDraft((d) => ({ ...d, response_format }))}
                onPick={(k) => setOpenSection(
                  k === 'structured' ? (openSection === 'output' ? null : 'output') : null,
                )}
              />
            </div>

            {openSection && (
              <div className="max-w-2xl border-t border-[color:var(--border-subtle)] pt-3">
                {openSection === 'model' && (
                  <ModelConfigPanel
                    value={draft.model || {}}
                    disabled={saving || !canEdit}
                    integrations={integrations}
                    modelsForCredential={modelsForCredential}
                    onChange={(model) => setDraft((d) => ({ ...d, model }))}
                  />
                )}
                {openSection === 'variables' && (
                  <VariablesPanel
                    variables={draft.variables}
                    innerNames={sectionInnerNames(draft.messages)}
                    onChange={(variables) => setDraft((d) => ({ ...d, variables }))}
                    disabled={saving || !canEdit}
                  />
                )}
                {openSection === 'tools' && (
                  <JsonEditor
                    value={draft.tools || []}
                    emptyValue={[]}
                    disabled={saving || !canEdit}
                    onChange={(tools) => setDraft((d) => ({ ...d, tools }))}
                  />
                )}
                {openSection === 'output' && (
                  <OutputSchemaEditor
                    value={draft.response_format ?? null}
                    disabled={saving || !canEdit}
                    onChange={(response_format) => setDraft((d) => ({ ...d, response_format }))}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Version history: activity timeline */}
        {versionsOpen && (
          <VersionTimeline
            versions={versions}
            members={members}
            onSelect={(v) => setSelectedVersion(v)}
            onClose={() => setVersionsOpen(false)}
          />
        )}
      </div>

      {/* Save-version modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30" onClick={() => setShowSaveModal(false)}>
          <div className="w-[420px] max-w-[92vw] rounded-xl border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-[14px] font-semibold text-[color:var(--text-primary)]">{isNew ? 'Create prompt' : 'Save new version'}</h2>
              <button type="button" onClick={() => setShowSaveModal(false)} aria-label="Close" title="Close" className={drawerIconButtonClass}><X size={15} /></button>
            </div>

            {/* GitHub-style line diff of the loaded version → what's being saved. */}
            <div className="mb-3">
              <span className={labelCls}>
                {baselineHash
                  ? `Changes since ${baselineHash}`
                  : `${isNew ? trimmedName || 'New prompt' : 'New prompt'}: full contents`}
              </span>
              <DiffView diff={diff} />
            </div>

            <label className="block">
              <span className={labelCls}>Commit message</span>
              <input autoFocus value={commitMessage} onChange={(e) => setCommitMessage(e.target.value)} placeholder="what changed…" className={inputCls} />
            </label>

            <div className="mt-3">
              <span className={labelCls}>Apply a label (optional)</span>
              {!addingLabel ? (
                <BrandSelect
                  value={chosenLabel}
                  minWidth={0}
                                    options={[
                    { value: '', label: 'No label' },
                    ...teamLabels.map((l) => ({ value: l, label: l })),
                    { value: '__new__', label: 'New label…' },
                  ]}
                  onChange={(v) => {
                    setConfirmMove(false);
                    if (v === '__new__') { setAddingLabel(true); setNewLabel(''); }
                    else setChosenLabel(v);
                  }}
                />
              ) : (
                <div className="flex items-center gap-2">
                  <input autoFocus value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') confirmAddLabel(); if (e.key === 'Escape') setAddingLabel(false); }}
                    placeholder="e.g. canary" className={`${inputCls} flex-1`} />
                  <button type="button" onClick={confirmAddLabel} className={primaryBtnCls}>Add</button>
                  <button type="button" onClick={() => setAddingLabel(false)} className={neutralBtnCls}>Cancel</button>
                </div>
              )}
              {labelMoves && !confirmMove && (
                <div className="mt-1.5 text-[11px] text-[color:var(--text-muted)]">
                  <span className="font-medium">{chosenLabel}</span> currently points at <span className="font-mono">{shortHash(labelHolder.version_hash)}</span>. Saving moves it here.
                </div>
              )}
            </div>

            {confirmMove && labelHolder && (
              <div className="mt-3 border-t border-[color:var(--border-subtle)] pt-3 text-[12px] leading-relaxed text-[color:var(--text-secondary)]">
                Move <span className="font-medium">{chosenLabel}</span> off <span className="font-mono">{shortHash(labelHolder.version_hash)}</span> and onto the version you&rsquo;re saving now?
                {' '}<span className="font-mono">{shortHash(labelHolder.version_hash)}</span> will no longer be labelled <span className="font-medium">{chosenLabel}</span>.
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              {confirmMove ? (
                <>
                  <button type="button" onClick={() => setConfirmMove(false)} className={neutralBtnCls}>Back</button>
                  <button type="button" onClick={handleSaveVersion} disabled={saving}
                    className={`${primaryBtnCls} gap-1.5`}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                    Confirm and push to {chosenLabel}
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => setShowSaveModal(false)} className={neutralBtnCls}>Cancel</button>
                  <button type="button"
                    onClick={() => { if (labelMoves) setConfirmMove(true); else handleSaveVersion(); }}
                    disabled={saving || addingLabel}
                    className={`${primaryBtnCls} gap-1.5`}>
                    {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                    {chosenLabel
                      ? `${isNew ? 'Create' : 'Save'} and push to ${chosenLabel}`
                      : (isNew ? 'Create prompt' : 'Save new version')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showCodeModal && (
        <SdkCodeModal
          name={name}
          variables={draft.variables}
          messages={draft.messages}
          selector={codeSelector}
          model={draft.model}
          tools={draft.tools}
          responseFormat={draft.response_format}
          onClose={() => setShowCodeModal(false)}
        />
      )}

      {/* Version detail popup: view contents, load into editor, or diff.
          Re-resolve against the live `versions` list (not the stale click-time
          snapshot) so a label move reflects immediately after `load()`. */}
      {selectedVersion && (
        <VersionPopup
          version={versions.find((v) => v.version_hash === selectedVersion.version_hash) || selectedVersion}
          versions={versions}
          members={members}
          canEdit={canEdit}
          teamLabels={teamLabels}
          onClose={() => setSelectedVersion(null)}
          onLoad={loadVersionIntoEditor}
          onToggleLabel={toggleLabelOnVersion}
          onCreateLabel={createTeamLabel}
        />
      )}

      {/* Per-prompt access: the owner invites team members as editors. */}
      {showMembers && (
        <MembersPanel
          promptId={promptId}
          ownerId={ownerId}
          members={members}
          isOwner={isOwner}
          onClose={() => setShowMembers(false)}
        />
      )}
    </div>
  );
}
